/**
 * Specialized Error Boundary Components
 *
 * These error boundaries are tailored for specific use cases like
 * video players, forms, async loading, and route sections.
 */

import React, { Component, ErrorInfo, ReactNode, useCallback } from 'react';
import { reportBoundaryError } from '@/services/errorReporting';
import {
  BaseErrorFallback,
  VideoErrorFallback,
  FormErrorFallback,
  AsyncErrorFallback,
  PageErrorFallback,
  ErrorFallbackProps,
} from './ErrorFallbackUI';

// ============================================================================
// Base Error Boundary Class
// ============================================================================

interface BaseErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((props: ErrorFallbackProps) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
  maxRetries?: number;
  resetKeys?: Array<string | number>;
  boundaryName?: string;
}

interface BaseErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
}

/**
 * Enhanced base error boundary with retry logic and error reporting
 */
class BaseErrorBoundaryClass extends Component<BaseErrorBoundaryProps, BaseErrorBoundaryState> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: BaseErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: BaseErrorBoundaryProps) {
    // Reset error state if resetKeys change
    if (this.props.resetKeys && prevProps.resetKeys) {
      const keysChanged = this.props.resetKeys.some(
        (key, index) => key !== prevProps.resetKeys![index]
      );
      if (keysChanged && this.state.hasError) {
        this.handleReset();
      }
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { boundaryName = 'ErrorBoundary', maxRetries = 3, onError } = this.props;

    this.setState({ errorInfo });

    // Report error to logging service
    reportBoundaryError(error, errorInfo, boundaryName, {
      retryCount: this.state.retryCount,
      maxRetries,
    });

    // Call custom error handler
    onError?.(error, errorInfo);

    // Check for auto-recoverable errors
    if (this.state.retryCount < maxRetries && this.isRecoverableError(error)) {
      this.attemptAutoRetry();
    }
  }

  isRecoverableError(error: Error): boolean {
    const recoverablePatterns = [
      'network',
      'fetch',
      'timeout',
      'loading chunk failed',
      'dynamically imported module',
      'failed to fetch',
      'load failed',
    ];

    const message = error.message.toLowerCase();
    return recoverablePatterns.some(pattern => message.includes(pattern));
  }

  attemptAutoRetry = () => {
    const delay = 1000 * Math.pow(2, this.state.retryCount); // Exponential backoff

    this.setState({ isRetrying: true });

    this.retryTimeout = setTimeout(() => {
      this.setState(prev => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prev.retryCount + 1,
        isRetrying: false,
      }));
    }, delay);
  };

  handleReset = () => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isRetrying: false,
    });

    this.props.onReset?.();
  };

  handleRetry = () => {
    this.setState(prev => ({ retryCount: prev.retryCount + 1 }));
    this.handleReset();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    const { hasError, error, errorInfo, retryCount, isRetrying } = this.state;
    const { children, fallback, maxRetries = 3 } = this.props;

    if (hasError) {
      const fallbackProps: ErrorFallbackProps = {
        error,
        errorInfo,
        onRetry: this.handleRetry,
        onGoHome: this.handleGoHome,
        onGoBack: this.handleGoBack,
        retryCount,
        maxRetries,
        isRetrying,
      };

      // Custom fallback
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback(fallbackProps);
        }
        return fallback;
      }

      // Default fallback
      return <BaseErrorFallback {...fallbackProps} />;
    }

    return children;
  }
}

// ============================================================================
// Specialized Error Boundaries
// ============================================================================

/**
 * Video Error Boundary - For video players and recorders
 */
interface VideoErrorBoundaryProps extends Omit<BaseErrorBoundaryProps, 'fallback'> {
  isRecording?: boolean;
  videoSrc?: string;
  fallbackClassName?: string;
}

export class VideoErrorBoundary extends Component<VideoErrorBoundaryProps, BaseErrorBoundaryState> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: VideoErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, isRecording } = this.props;

    this.setState({ errorInfo });

    reportBoundaryError(error, errorInfo, 'VideoErrorBoundary', {
      isRecording,
      errorType: error.name,
    });

    onError?.(error, errorInfo);
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  handleRetry = () => {
    this.setState(prev => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prev.retryCount + 1,
      isRetrying: false,
    }));
    this.props.onReset?.();
  };

  render() {
    const { hasError, error, errorInfo, retryCount, isRetrying } = this.state;
    const { children, isRecording, videoSrc, maxRetries = 3, fallbackClassName } = this.props;

    if (hasError) {
      return (
        <div className={fallbackClassName}>
          <VideoErrorFallback
            error={error}
            errorInfo={errorInfo}
            onRetry={this.handleRetry}
            isRecording={isRecording}
            videoSrc={videoSrc}
            retryCount={retryCount}
            maxRetries={maxRetries}
            isRetrying={isRetrying}
          />
        </div>
      );
    }

    return children;
  }
}

/**
 * Form Error Boundary - For form components
 */
interface FormErrorBoundaryProps extends Omit<BaseErrorBoundaryProps, 'fallback'> {
  formName?: string;
  onDismiss?: () => void;
}

export class FormErrorBoundary extends Component<FormErrorBoundaryProps, BaseErrorBoundaryState> {
  constructor(props: FormErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, formName } = this.props;

    this.setState({ errorInfo });

    reportBoundaryError(error, errorInfo, 'FormErrorBoundary', {
      formName,
    });

    onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    this.props.onReset?.();
  };

  handleDismiss = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    this.props.onDismiss?.();
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, formName } = this.props;

    if (hasError) {
      return (
        <FormErrorFallback
          error={error}
          errorInfo={errorInfo}
          onRetry={this.handleRetry}
          onDismiss={this.handleDismiss}
          formName={formName}
        />
      );
    }

    return children;
  }
}

/**
 * Async Error Boundary - For async-loaded content (Suspense fallback scenarios)
 */
interface AsyncErrorBoundaryProps extends Omit<BaseErrorBoundaryProps, 'fallback'> {
  resourceName?: string;
}

export class AsyncErrorBoundary extends Component<AsyncErrorBoundaryProps, BaseErrorBoundaryState> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: AsyncErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, resourceName, maxRetries = 3 } = this.props;

    this.setState({ errorInfo });

    reportBoundaryError(error, errorInfo, 'AsyncErrorBoundary', {
      resourceName,
      retryCount: this.state.retryCount,
    });

    onError?.(error, errorInfo);

    // Auto-retry for chunk loading errors
    if (this.state.retryCount < maxRetries && this.isChunkLoadError(error)) {
      this.attemptAutoRetry();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  isChunkLoadError(error: Error): boolean {
    const message = error.message.toLowerCase();
    return message.includes('loading chunk') ||
           message.includes('dynamically imported module') ||
           message.includes('failed to fetch');
  }

  attemptAutoRetry = () => {
    const delay = 1000 * Math.pow(2, this.state.retryCount);

    this.setState({ isRetrying: true });

    this.retryTimeout = setTimeout(() => {
      this.setState(prev => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prev.retryCount + 1,
        isRetrying: false,
      }));
    }, delay);
  };

  handleRetry = () => {
    this.setState(prev => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prev.retryCount + 1,
      isRetrying: false,
    }));
    this.props.onReset?.();
  };

  render() {
    const { hasError, error, errorInfo, retryCount, isRetrying } = this.state;
    const { children, resourceName, maxRetries = 3 } = this.props;

    if (hasError) {
      return (
        <AsyncErrorFallback
          error={error}
          errorInfo={errorInfo}
          onRetry={this.handleRetry}
          resourceName={resourceName}
          retryCount={retryCount}
          maxRetries={maxRetries}
          isRetrying={isRetrying}
        />
      );
    }

    return children;
  }
}

/**
 * Route Error Boundary - For route-level error handling
 */
interface RouteErrorBoundaryProps extends Omit<BaseErrorBoundaryProps, 'fallback'> {
  routeName?: string;
}

export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, BaseErrorBoundaryState> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, routeName } = this.props;

    this.setState({ errorInfo });

    reportBoundaryError(error, errorInfo, 'RouteErrorBoundary', {
      routeName,
      url: window.location.href,
      pathname: window.location.pathname,
    });

    onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    this.props.onReset?.();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    const { hasError, error, errorInfo, retryCount } = this.state;
    const { children, routeName, maxRetries = 3 } = this.props;

    if (hasError) {
      return (
        <PageErrorFallback
          error={error}
          errorInfo={errorInfo}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
          onGoBack={this.handleGoBack}
          retryCount={retryCount}
          maxRetries={maxRetries}
          title={`Error loading ${routeName || 'page'}`}
          message="There was a problem loading this page. Please try again."
        />
      );
    }

    return children;
  }
}

/**
 * Critical Section Error Boundary - For sections that must not fail silently
 */
interface CriticalErrorBoundaryProps extends BaseErrorBoundaryProps {
  sectionName: string;
  onCriticalError?: (error: Error) => void;
}

export class CriticalErrorBoundary extends Component<CriticalErrorBoundaryProps, BaseErrorBoundaryState> {
  constructor(props: CriticalErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<BaseErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, onCriticalError, sectionName } = this.props;

    this.setState({ errorInfo });

    // Report as critical error
    reportBoundaryError(error, errorInfo, 'CriticalErrorBoundary', {
      sectionName,
      severity: 'critical',
    });

    onError?.(error, errorInfo);
    onCriticalError?.(error);
  }

  handleRetry = () => {
    this.setState(prev => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prev.retryCount + 1,
    }));
    this.props.onReset?.();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    const { hasError, error, errorInfo, retryCount } = this.state;
    const { children, sectionName, fallback, maxRetries = 3 } = this.props;

    if (hasError) {
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback({
            error,
            errorInfo,
            onRetry: this.handleRetry,
            onGoHome: this.handleGoHome,
            retryCount,
            maxRetries,
          });
        }
        return fallback;
      }

      return (
        <BaseErrorFallback
          error={error}
          errorInfo={errorInfo}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
          retryCount={retryCount}
          maxRetries={maxRetries}
          title={`Critical Error in ${sectionName}`}
          message="A critical error has occurred. Please try again or return to the home page."
        />
      );
    }

    return children;
  }
}

// ============================================================================
// Hook-based Error Boundary Wrapper
// ============================================================================

/**
 * Hook to create error boundary reset functionality
 */
export function useErrorBoundaryReset(): [number, () => void] {
  const [resetKey, setResetKey] = React.useState(0);

  const reset = useCallback(() => {
    setResetKey(prev => prev + 1);
  }, []);

  return [resetKey, reset];
}

/**
 * Higher-order component to wrap a component with an error boundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  boundaryProps?: Partial<BaseErrorBoundaryProps>
): React.FC<P> {
  const WithErrorBoundary: React.FC<P> = (props) => {
    return (
      <BaseErrorBoundaryClass {...boundaryProps}>
        <WrappedComponent {...props} />
      </BaseErrorBoundaryClass>
    );
  };

  WithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithErrorBoundary;
}

// Export the base class for custom implementations
export { BaseErrorBoundaryClass };
